/**
 * CheckinModal Component
 *
 * Modal para realizar check-in em um local.
 * Fluxo:
 * 1. Buscar local por GPS ou texto
 * 2. Selecionar local na lista
 * 3. Confirmar check-in
 *
 * @since 2025-12-17
 * @module tmf_checkin
 */

import { useState, useCallback, useEffect } from 'react';
import { Modal } from '@/components/ui';
import { CheckinSearch } from './CheckinSearch';
import { CheckinLocationList } from './CheckinLocationList';
import { CheckinTaskList } from './CheckinTaskList';
import { CheckinExecutionReport } from './CheckinExecutionReport';
import { useCheckin } from '@/hooks/useCheckin';
import { useAuth } from '@/contexts/AuthContext';
import type { NearbyLocation, Checkin } from '@/types/checkin';
import { formatDistance, formatDuration } from '@/services/checkinApi';
import clsx from 'clsx';

type ModalStep = 'search' | 'confirm' | 'active' | 'tasks' | 'report' | 'success';

interface CheckinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckinComplete?: (checkin: Checkin) => void;
  onCheckoutComplete?: (checkin: Checkin) => void;
}

export function CheckinModal({
  isOpen,
  onClose,
  onCheckinComplete,
  onCheckoutComplete,
}: CheckinModalProps) {
  const { user } = useAuth();
  const personId = user?.id || 0;

  const {
    // State
    position,
    positionLoading,
    positionError,
    positionSupported,
    activeCheckin,
    activeCheckinLoading,
    locations,
    locationsLoading,
    locationsError,
    mutationLoading,
    mutationError,
    offlinePendingCount,
    // Actions
    refreshPosition,
    doCheckin,
    doCheckout,
    searchNearby,
    searchByText,
    resetLocations,
    syncOffline,
  } = useCheckin({ personId, autoRefresh: true, refreshInterval: 30000 });

  // Local state
  const [step, setStep] = useState<ModalStep>('search');
  const [searchValue, setSearchValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<NearbyLocation | null>(null);
  const [observation, setObservation] = useState('');
  const [successCheckin, setSuccessCheckin] = useState<Checkin | null>(null);
  const [gpsSearchError, setGpsSearchError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if there's an active checkin
      if (activeCheckin && !activeCheckinLoading) {
        setStep('active');
      } else {
        setStep('search');
      }
      setSearchValue('');
      setSelectedLocation(null);
      setObservation('');
      setSuccessCheckin(null);
      setGpsSearchError(null);
      resetLocations();
    }
  }, [isOpen, activeCheckin, activeCheckinLoading, resetLocations]);

  // Update step when activeCheckin changes
  useEffect(() => {
    if (isOpen && !activeCheckinLoading) {
      if (activeCheckin && step === 'search') {
        setStep('active');
      } else if (!activeCheckin && step === 'active') {
        setStep('search');
      }
    }
  }, [activeCheckin, activeCheckinLoading, isOpen, step]);

  // Handle GPS search
  const handleGpsSearch = useCallback(async () => {
    setGpsSearchError(null);
    try {
      if (!position) {
        refreshPosition();
        return;
      }
      await searchNearby({ radius: 5000, limit: 20 });
    } catch (err) {
      console.error('Erro ao buscar por GPS:', err);
      // Mostrar mensagem amigável para o usuário
      if (err instanceof Error) {
        if (err.message.includes('timeout') || err.message.includes('exceeded')) {
          setGpsSearchError('A busca demorou muito. Tente novamente ou busque pelo nome do local.');
        } else if (err.message.includes('Network Error') || err.message.includes('network')) {
          setGpsSearchError('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
        } else {
          setGpsSearchError('Erro ao buscar locais próximos. Tente novamente.');
        }
      } else {
        setGpsSearchError('Erro ao buscar locais próximos. Tente novamente.');
      }
    }
  }, [position, refreshPosition, searchNearby]);

  // Auto search by GPS when position becomes available
  useEffect(() => {
    if (isOpen && position && !positionLoading && locations.length === 0 && step === 'search' && !gpsSearchError) {
      handleGpsSearch();
    }
  }, [isOpen, position, positionLoading, locations.length, step, handleGpsSearch, gpsSearchError]);

  // Handle text search
  const handleTextSearch = useCallback(
    async (text: string) => {
      if (text.length >= 2) {
        try {
          await searchByText(text, 20);
        } catch (err) {
          console.error('Erro ao buscar por texto:', err);
        }
      }
    },
    [searchByText]
  );

  // Handle location select
  const handleSelectLocation = useCallback((location: NearbyLocation) => {
    setSelectedLocation(location);
    setStep('confirm');
  }, []);

  // Handle check-in
  const handleCheckin = useCallback(async () => {
    if (!selectedLocation) return;

    try {
      const result = await doCheckin(selectedLocation.id, observation || undefined);

      if (result.success) {
        if (result.offline) {
          // Offline - show success with offline indicator
          setSuccessCheckin(null);
          setStep('success');
        } else if (result.checkin) {
          // Online - show success
          setSuccessCheckin(result.checkin);
          setStep('success');
          onCheckinComplete?.(result.checkin);
        }
      }
    } catch (err) {
      console.error('Erro ao fazer check-in:', err);
    }
  }, [selectedLocation, observation, doCheckin, onCheckinComplete]);

  // Handle checkout - agora vai para o relatório
  const handleCheckout = useCallback(async () => {
    try {
      const checkin = await doCheckout(observation || undefined);
      setSuccessCheckin(checkin);
      setStep('report'); // Vai para o relatório em vez de success
      onCheckoutComplete?.(checkin);
    } catch (err) {
      console.error('Erro ao fazer checkout:', err);
    }
  }, [observation, doCheckout, onCheckoutComplete]);

  // Navegar para ver tarefas
  const handleViewTasks = useCallback(() => {
    setStep('tasks');
  }, []);

  // Voltar das tarefas para o checkin ativo
  const handleBackFromTasks = useCallback(() => {
    setStep('active');
  }, []);

  // Handle back
  const handleBack = useCallback(() => {
    if (step === 'confirm') {
      setSelectedLocation(null);
      setStep('search');
    } else if (step === 'success') {
      onClose();
    }
  }, [step, onClose]);

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 'search':
        return (
          <div className="space-y-4">
            <CheckinSearch
              value={searchValue}
              onChange={setSearchValue}
              onSearch={handleTextSearch}
              onGpsSearch={handleGpsSearch}
              loading={locationsLoading}
              gpsLoading={positionLoading}
              gpsError={gpsSearchError || positionError?.message || (!positionSupported ? 'GPS não suportado' : null)}
              autoFocus
            />

            <div className="max-h-80 overflow-y-auto -mx-4">
              <CheckinLocationList
                locations={locations}
                loading={locationsLoading}
                error={locationsError}
                onSelect={handleSelectLocation}
                emptyMessage={
                  searchValue
                    ? 'Nenhum local encontrado para esta busca'
                    : 'Busque por nome ou use o GPS'
                }
              />
            </div>

            {/* Offline indicator */}
            {offlinePendingCount > 0 && (
              <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-500/10 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="bi bi-cloud-upload text-yellow-600 dark:text-yellow-400" />
                  <span className="text-sm text-yellow-700 dark:text-yellow-400">
                    {offlinePendingCount} check-in(s) pendente(s)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => syncOffline()}
                  className="text-sm text-yellow-600 hover:text-yellow-700 font-medium"
                >
                  Sincronizar
                </button>
              </div>
            )}
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-4">
            {/* Selected location card */}
            <div className="p-4 bg-orange-50 dark:bg-orange-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-geo-alt-fill text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground">
                    {selectedLocation?.resource_name}
                  </h4>
                  {selectedLocation?.code && (
                    <p className="text-sm text-muted-foreground">#{selectedLocation.code}</p>
                  )}
                  {selectedLocation?.address && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedLocation.address.address}
                      {selectedLocation.address.number && `, ${selectedLocation.address.number}`}
                      {selectedLocation.address.city && ` - ${selectedLocation.address.city}`}
                    </p>
                  )}
                  <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                    {formatDistance(selectedLocation?.distance_meters || 0)} de distância
                  </p>
                </div>
              </div>
            </div>

            {/* Observation field */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                Observação (opcional)
              </label>
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Adicione uma observação sobre esta visita..."
                rows={3}
                className="w-full px-3 py-2 border border-border bg-card text-foreground placeholder-muted-foreground rounded-lg text-sm focus:outline-none focus:border-orange-500 resize-none"
              />
            </div>

            {/* Error message */}
            {mutationError && (
              <div className="p-3 bg-red-50 dark:bg-red-500/10 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{mutationError}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleBack}
                disabled={mutationLoading}
                className="flex-1 px-4 py-2.5 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleCheckin}
                disabled={mutationLoading}
                className={clsx(
                  'flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium transition-colors',
                  mutationLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-orange-600'
                )}
              >
                {mutationLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Fazendo check-in...
                  </span>
                ) : (
                  'Confirmar Check-in'
                )}
              </button>
            </div>
          </div>
        );

      case 'active':
        return (
          <div className="space-y-4">
            {/* Active checkin card */}
            <div className="p-4 bg-green-50 dark:bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Check-in ativo</span>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-geo-alt-fill text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground">
                    {activeCheckin?.resource?.resource_name || 'Local'}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Check-in às{' '}
                    {activeCheckin?.datetime_checkin &&
                      new Date(activeCheckin.datetime_checkin).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                  </p>
                  {activeCheckin?.duration_minutes && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Duração: {formatDuration(activeCheckin.duration_minutes)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Observation field for checkout */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                Observação do checkout (opcional)
              </label>
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Adicione uma observação sobre esta visita..."
                rows={3}
                className="w-full px-3 py-2 border border-border bg-card text-foreground placeholder-muted-foreground rounded-lg text-sm focus:outline-none focus:border-orange-500 resize-none"
              />
            </div>

            {/* Error message */}
            {mutationError && (
              <div className="p-3 bg-red-50 dark:bg-red-500/10 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{mutationError}</p>
              </div>
            )}

            {/* Tarefas da visita (preview compacto) */}
            {activeCheckin?.id && (
              <div className="border-t border-border pt-4">
                <CheckinTaskList checkinId={activeCheckin.id} compact />
                <button
                  type="button"
                  onClick={handleViewTasks}
                  className="mt-2 w-full text-sm text-orange-500 hover:text-orange-600 font-medium"
                >
                  Ver todas as tarefas
                </button>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={mutationLoading}
                className="flex-1 px-4 py-2.5 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={handleCheckout}
                disabled={mutationLoading}
                className={clsx(
                  'flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium transition-colors',
                  mutationLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-red-600'
                )}
              >
                {mutationLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Fazendo checkout...
                  </span>
                ) : (
                  'Fazer Checkout'
                )}
              </button>
            </div>
          </div>
        );

      case 'tasks':
        return (
          <div className="space-y-4">
            {/* Header com informações do local */}
            <div className="p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {activeCheckin?.resource?.resource_name || 'Check-in ativo'}
                </span>
              </div>
            </div>

            {/* Lista completa de tarefas */}
            {activeCheckin?.id && (
              <div className="max-h-80 overflow-y-auto">
                <CheckinTaskList checkinId={activeCheckin.id} />
              </div>
            )}

            {/* Botão voltar */}
            <button
              type="button"
              onClick={handleBackFromTasks}
              className="w-full px-4 py-2.5 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              Voltar
            </button>
          </div>
        );

      case 'report':
        return successCheckin?.id ? (
          <CheckinExecutionReport
            checkinId={successCheckin.id}
            onClose={onClose}
          />
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground">Carregando relatório...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="bi bi-check-lg text-green-500 dark:text-green-400 text-3xl" />
            </div>

            <h3 className="text-lg font-semibold text-foreground mb-2">
              {successCheckin?.datetime_checkout ? 'Checkout realizado!' : 'Check-in realizado!'}
            </h3>

            <p className="text-muted-foreground mb-6">
              {successCheckin?.datetime_checkout
                ? `Você fez checkout de ${successCheckin?.resource?.resource_name}`
                : successCheckin
                ? `Você fez check-in em ${successCheckin?.resource?.resource_name}`
                : 'Seu check-in foi salvo e será sincronizado assim que houver conexão.'}
            </p>

            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              Fechar
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // Modal title based on step
  const getTitle = () => {
    switch (step) {
      case 'search':
        return 'Fazer Check-in';
      case 'confirm':
        return 'Confirmar Check-in';
      case 'active':
        return 'Check-in Ativo';
      case 'tasks':
        return 'Tarefas da Visita';
      case 'report':
        return 'Relatório da Visita';
      case 'success':
        return '';
      default:
        return 'Check-in';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      size="md"
      showCloseButton={step !== 'success'}
    >
      {activeCheckinLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">Carregando...</p>
        </div>
      ) : (
        renderStep()
      )}
    </Modal>
  );
}

export default CheckinModal;
